/* Device command queue (ZKTeco push / StartHR Agent). */
import { useState } from "react";
import type { Device, DeviceCommand } from "../../../data/types";
import { useCommands, useQueueCommand, useSyncEmployeesToDevice } from "../../../data/hooks";
import { Alert, Button, Chip, Confirm, EmptyState, Loaded, Select, errMsg, useToast } from "../../../ui";
import { useI18n } from "../../../i18n";
import { commandLabel, commandStatusLabel, hm } from "./labels";

type NewKind = "sync_users" | "query_logs" | "reboot" | "info" | "set_time" | "clear_logs";
const DANGER: NewKind[] = ["reboot", "clear_logs"];

export function CommandsPanel({ device, today }: { device: Device; today: string }) {
  const { L } = useI18n();
  const toast = useToast();
  const cmds = useCommands(device.id);
  const queue = useQueueCommand();
  const pushUsers = useSyncEmployeesToDevice();
  const [kind, setKind] = useState<NewKind>("info");
  const [confirm, setConfirm] = useState(false);
  const supported = device.protocol === "push" || device.protocol === "agent";
  const opts: [NewKind, string][] = [
    ["sync_users", L("Sync users (all active employees)", "مزامنة المستخدمين (كل الموظفين النشطين)")],
    ["query_logs", L("Query today's logs", "طلب سجلات اليوم")], ["reboot", L("Reboot", "إعادة تشغيل")], ["info", L("Get info", "جلب المعلومات")],
    ["set_time", L("Set time", "ضبط الوقت")], ["clear_logs", L("Clear logs on device", "مسح السجلات من الجهاز")],
  ];

  const run = () => {
    setConfirm(false);
    if (kind === "sync_users") {
      pushUsers.mutate({ deviceId: device.id }, { onSuccess: (r) => toast(L(`${r.length} employees queued for the device`, `تمت جدولة ${r.length} موظفًا للجهاز`)), onError: (e) => toast(errMsg(e), "bad") });
      return;
    }
    const payload: Record<string, string> = kind === "query_logs" ? { start: today + " 00:00:00", end: today + " 23:59:59" } : {};
    queue.mutate({ deviceId: device.id, kind: kind as DeviceCommand["kind"], payload }, {
      onSuccess: (c) => toast(L(`“${commandLabel(c.kind, L)}” queued — sent at the device's next check-in`, `تمت جدولة «${commandLabel(c.kind, L)}» — يُرسل عند اتصال الجهاز التالي`)),
      onError: (e) => toast(errMsg(e), "bad"),
    });
  };

  return (
    <div className="stack" data-testid="commands-panel">
      {!supported && <Alert tone="info" tint title={L("Commands are only available for ZKTeco cloud (push) devices and the StartHR Agent.", "الأوامر متاحة فقط لأجهزة ZKTeco السحابية ووكيل ستارت HR.")} testId="commands-unsupported" />}
      {supported && (
        <div className="time-toolbar">
          <Select value={kind} onChange={(e) => setKind(e.target.value as NewKind)} options={opts} aria-label={L("New command", "أمر جديد")} data-testid="cmd-kind" />
          <Button variant="pri" icon="plus" onClick={() => (DANGER.includes(kind) ? setConfirm(true) : run())} loading={queue.isPending || pushUsers.isPending} data-testid="btn-queue-cmd">{L("Queue command", "جدولة الأمر")}</Button>
        </div>
      )}
      <Loaded q={cmds}>{(list) => list.length === 0 ? <EmptyState title={L("No commands yet", "لا توجد أوامر بعد")} desc={L("Commands wait here until the device checks in (usually within 10 seconds).", "تنتظر الأوامر هنا حتى يتصل الجهاز (عادة خلال 10 ثوانٍ).")} /> : (
        <div className="tw"><table className="tbl cards compact" data-testid="commands-table">
          <thead><tr><th>#</th><th>{L("Command", "الأمر")}</th><th>{L("Status", "الحالة")}</th><th className="hide-md">{L("Queued", "وقت الجدولة")}</th><th className="hide-md">{L("Done", "وقت التنفيذ")}</th><th className="hide-md">{L("Result", "النتيجة")}</th></tr></thead>
          <tbody>{list.slice().reverse().map((c) => (
            <tr key={c.id} data-testid={"cmd-" + c.id}>
              <td className="k3 num" data-l="#">{c.id}</td>
              <td className="k1"><b>{commandLabel(c.kind, L)}</b>{c.payload.pin && <span className="xs muted"> · PIN {c.payload.pin}{c.payload.name ? " · " + c.payload.name : ""}</span>}</td>
              <td className="k2"><Chip status={c.status}>{commandStatusLabel(c.status, L)}</Chip></td>
              <td className="hide-md num sm">{c.createdAt.slice(0, 10)} {hm(c.createdAt)}</td>
              <td className="hide-md num sm">{c.doneAt ? hm(c.doneAt) : "—"}</td>
              <td className="hide-md sm muted" dir="ltr" style={{ maxWidth: 280, overflowWrap: "anywhere" }}>{c.result || "—"}</td>
            </tr>
          ))}</tbody>
        </table></div>
      )}</Loaded>
      {confirm && <Confirm title={L("Send this command?", "إرسال هذا الأمر؟")} danger confirmLabel={L("Queue command", "جدولة الأمر")} onClose={() => setConfirm(false)} onConfirm={run}
        body={kind === "clear_logs" ? L("The device will delete its stored punches. Punches already in StartHR are kept.", "سيحذف الجهاز البصمات المخزنة فيه. البصمات الموجودة في ستارت HR لن تتأثر.") : L("The device restarts and is offline for about a minute.", "سيُعاد تشغيل الجهاز ويبقى غير متصل لنحو دقيقة.")} />}
    </div>
  );
}
